import { cn } from '@my/ui';
import { Plus } from 'lucide-react';
import { roleCounts, summariseAppliesWhen, type Catalogue } from '../lib/conditions';
import { ROLES, type CheckpointTemplate } from '../lib/model';
import { RoleTag } from './roles';

export function TemplateList({
  templates,
  selectedId,
  onSelect,
  onAdd,
  catalogue,
  dirtyIds,
}: {
  templates: readonly CheckpointTemplate[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAdd: () => void;
  catalogue: Catalogue;
  /** Templates with unsaved changes — marked so a Save is never a surprise. */
  dirtyIds: ReadonlySet<string>;
}) {
  return (
    <aside className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Templates</h3>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900"
        >
          <Plus className="h-3.5 w-3.5" /> New
        </button>
      </div>
      {templates.length === 0 && (
        <p className="rounded-lg border border-dashed border-slate-300 px-3 py-4 text-center text-[11px] text-slate-500">
          No templates yet.
        </p>
      )}
      <ul className="space-y-1.5">
        {templates.map((t) => {
          const counts = roleCounts(t);
          return (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => onSelect(t.id)}
                className={cn(
                  'w-full rounded-lg border px-3 py-2 text-left transition-colors',
                  t.id === selectedId ? 'border-sky-300 bg-sky-50' : 'border-slate-200 bg-white hover:bg-slate-50',
                  !t.active && 'opacity-60',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium text-slate-800">{t.name || 'Untitled'}</span>
                  <span className="flex shrink-0 items-center gap-1">
                    {dirtyIds.has(t.id) && (
                      <span title="Unsaved changes" className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    )}
                    {!t.active && <span className="text-[10px] text-slate-500">Off</span>}
                  </span>
                </div>
                <p className="mt-0.5 truncate text-[11px] text-slate-500">{summariseAppliesWhen(t.appliesWhen, catalogue)}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                  {ROLES.filter((r) => counts[r] > 0).map((r) => (
                    <span key={r} className="inline-flex items-center gap-0.5 text-[10px] text-slate-500">
                      <RoleTag role={r} />
                      {counts[r]}
                    </span>
                  ))}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
